import React, { useState, useEffect, useContext, useCallback, useMemo, memo } from 'react';
import { toast } from '../../utils/toast';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Container,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  Button,
  Menu,
  MenuItem,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Skeleton,
  Zoom,
  Grow,
  Slide,
  Badge,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  FormControl,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  ShoppingCart,
  People,
  Assessment,
  Download,
  Warning,
  Inventory,
  Receipt,
  BarChart,
  CompareArrows,
  Refresh,
  FileDownload,
  Insights,
  Analytics,
  TrendingFlat,
  Search,
  FilterList,
} from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import PageLayout from '../../components/layout/PageLayout';
import { DarkModeContext } from '../../context/darkModeContext';
import { ReportesApi } from '../../services/api/reportesApi';
import { ExportService } from '../../services/api/exportService';
import { ReportFilters } from '../../components/module/reportes/Filters/ReportFilters';
import { ReportesDataTable } from '../../components/module/reportes/Tables/ReportesDataTable';
import { VentasChart } from '../../components/module/reportes/Charts/VentasChart';
import {
  DashboardKPI,
  EstadisticaVentas,
  FiltroReporte,
  TopProducto,
  ProductoRentabilidad,
  ReporteVentasDetalle,
  ReporteImpuestos,
  ReporteInventario,
  ReporteDevoluciones,
  ReporteClientes,
  ReporteVendedores,
  AnalisisABC,
  ReporteAlertas,
} from '../../components/types/reportes.types';
import Swal from 'sweetalert2';
import './reportes.scss';

// ============================================
// TYPES & INTERFACES
// ============================================
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color?: 'primary' | 'success' | 'warning' | 'error';
  loading?: boolean;
  delay?: number;
}

interface StatusCardProps {
  value: number;
  label: string;
  type: 'paid' | 'pending' | 'cancelled';
  percentage?: number;
  loading?: boolean;
  delay?: number;
}

interface TableControlsProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  totalItems: number;
  searchPlaceholder?: string;
}

// ============================================
// CUSTOM HOOK: useTableControls
// ============================================
function useTableControls<T>({
  data,
  searchFields,
  initialRowsPerPage = 10,
}: {
  data: T[];
  searchFields: (keyof T)[];
  initialRowsPerPage?: number;
}) {
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Filtrar datos según búsqueda
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;

    const searchLower = searchValue.toLowerCase();

    return data.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (value == null) return false;

        return String(value).toLowerCase().includes(searchLower);
      })
    );
  }, [data, searchValue, searchFields]);

  // Calcular páginas totales
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Ajustar página actual si excede el total
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Paginar datos
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  // Handler para cambio de búsqueda (resetea página)
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  // Handler para cambio de filas por página (resetea página)
  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  return {
    searchValue,
    setSearchValue: handleSearchChange,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage: handleRowsPerPageChange,
    filteredData,
    paginatedData,
    totalPages,
    totalItems: filteredData.length,
  };
}

// ============================================
// THEME CONFIGURATION
// ============================================
// ============================================
// MEMOIZED COMPONENTS
// ============================================
const TabPanel: React.FC<TabPanelProps> = memo(({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
      {value === index && (
        <Fade in timeout={400}>
          <Box sx={{ pt: 3 }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
});

TabPanel.displayName = 'TabPanel';

// ============================================
// TABLE CONTROLS COMPONENT
// ============================================
const TableControls: React.FC<TableControlsProps> = memo(({
  searchValue,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  totalItems,
  searchPlaceholder = 'Buscar...',
}) => {
  const startItem = (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        {/* Búsqueda */}
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {/* Información de registros */}
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {totalItems > 0 ? startItem : 0}-{endItem} de {totalItems}
          </Typography>

          {/* Selector de filas por página */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              displayEmpty
            >
              <MenuItem value={10}>10 por página</MenuItem>
              <MenuItem value={25}>25 por página</MenuItem>
              <MenuItem value={50}>50 por página</MenuItem>
              <MenuItem value={100}>100 por página</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Paginación */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}

      {/* Indicador de búsqueda activa */}
      {searchValue && (
        <Box mt={2} display="flex" alignItems="center" gap={1}>
          <FilterList fontSize="small" color="primary" />
          <Typography variant="caption" color="primary.main" fontWeight={600}>
            Filtrando resultados por: "{searchValue}"
          </Typography>
          <Chip
            label="Limpiar"
            size="small"
            onClick={() => onSearchChange('')}
            onDelete={() => onSearchChange('')}
          />
        </Box>
      )}
    </Paper>
  );
});

TableControls.displayName = 'TableControls';

// ============================================
// KPI CARD COMPONENT
// ============================================
const KPICard: React.FC<KPICardProps> = memo(({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'primary',
  loading = false,
  delay = 0,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend > 0) return <TrendingUp />;
    if (trend < 0) return <TrendingDown />;
    return <TrendingFlat />;
  };

  if (loading) {
    return (
      <Card className="kpi-card" elevation={2}>
        <CardContent>
          <Skeleton variant="circular" width={64} height={64} />
          <Skeleton variant="text" sx={{ mt: 2, fontSize: '2rem' }} />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Zoom in={show} timeout={500} style={{ transitionDelay: `${delay}ms` }}>
      <Card className="kpi-card hover-lift" elevation={2}>
        <CardContent>
          <Box className="kpi-header">
            <Box className={`kpi-icon ${color}`}>{icon}</Box>
            {trend !== undefined && (
              <Tooltip title={`${trend >= 0 ? '+' : ''}${trend.toFixed(1)}% vs período anterior`} arrow>
                <Box className={`kpi-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
                  {getTrendIcon()}
                  <Typography variant="caption" fontWeight={700}>
                    {Math.abs(trend).toFixed(1)}%
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
          <Typography
            variant="h4"
            className="kpi-value"
            sx={{ mt: 2, mb: 0.5 }}
            data-value={value}
          >
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );
});

KPICard.displayName = 'KPICard';

// ============================================
// STATUS CARD COMPONENT
// ============================================
const StatusCard: React.FC<StatusCardProps> = memo(({
  value,
  label,
  type,
  percentage = 0,
  loading = false,
  delay = 0,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const getColor = () => {
    switch (type) {
      case 'paid': return 'success.main';
      case 'pending': return 'warning.main';
      case 'cancelled': return 'error.main';
      default: return 'text.primary';
    }
  };

  if (loading) {
    return (
      <Paper className={`status-card ${type}`} elevation={2} sx={{ p: 2.5 }}>
        <Skeleton variant="text" sx={{ fontSize: '3rem' }} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rectangular" height={6} sx={{ mt: 1, borderRadius: 1 }} />
      </Paper>
    );
  }

  return (
    <Grow in={show} timeout={600} style={{ transitionDelay: `${delay}ms` }}>
      <Paper className={`status-card ${type} hover-lift`} elevation={2} sx={{ p: 2.5 }}>
        <Box className="status-content">
          <Typography variant="h3" fontWeight={700} color={getColor()}>
            {value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>
            {label}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            sx={{ mt: 1.5, height: 6, borderRadius: 1 }}
            color={type === 'paid' ? 'success' : type === 'pending' ? 'warning' : 'error'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {percentage.toFixed(1)}% del total
          </Typography>
        </Box>
      </Paper>
    </Grow>
  );
});

StatusCard.displayName = 'StatusCard';

// ============================================
// LOADING SKELETON COMPONENT
// ============================================
const DashboardSkeleton: React.FC = memo(() => (
  <Container maxWidth="xl">
    <Box mb={3}>
      <Skeleton variant="text" width={300} height={40} />
      <Skeleton variant="text" width={200} height={20} />
    </Box>
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Card elevation={2}>
            <CardContent>
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton variant="text" sx={{ mt: 2, fontSize: '2rem' }} />
              <Skeleton variant="text" width="60%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Container>
));

DashboardSkeleton.displayName = 'DashboardSkeleton';

// ============================================
// MAIN COMPONENT
// ============================================
const Reportes: React.FC = () => {
  const { darkMode } = useContext(DarkModeContext);

  // Estados principales
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [currentExportType, setCurrentExportType] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Estados de datos
  const [dashboardKPIs, setDashboardKPIs] = useState<DashboardKPI | null>(null);
  const [estadisticas, setEstadisticas] = useState<EstadisticaVentas[]>([]);
  const [topProductos, setTopProductos] = useState<TopProducto[]>([]);
  const [rentabilidad, setRentabilidad] = useState<ProductoRentabilidad[]>([]);
  const [ventas, setVentas] = useState<ReporteVentasDetalle[]>([]);
  const [impuestos, setImpuestos] = useState<ReporteImpuestos[]>([]);
  const [inventario, setInventario] = useState<ReporteInventario[]>([]);
  const [devoluciones, setDevoluciones] = useState<ReporteDevoluciones[]>([]);
  const [clientes, setClientes] = useState<ReporteClientes[]>([]);
  const [vendedores, setVendedores] = useState<ReporteVendedores[]>([]);
  const [analisisABC, setAnalisisABC] = useState<AnalisisABC[]>([]);
  const [alertas, setAlertas] = useState<ReporteAlertas[]>([]);

  // ✅ NUEVOS: Hooks para paginación y búsqueda
  const ventasControls = useTableControls({
    data: ventas,
    searchFields: ['CodigoFacturaVenta', 'Cliente', 'Usuario', 'EstadoVenta'],
    initialRowsPerPage: 10,
  });

  const productosControls = useTableControls({
    data: topProductos,
    searchFields: ['Producto', 'Categoria', 'EAN'],
    initialRowsPerPage: 10,
  });

  const rentabilidadControls = useTableControls({
    data: rentabilidad,
    searchFields: ['Producto', 'Categoria', 'EAN'],
    initialRowsPerPage: 10,
  });

  const inventarioControls = useTableControls({
    data: inventario,
    searchFields: ['Producto', 'EAN', 'Categoria', 'Sucursal'],
    initialRowsPerPage: 25,
  });

  const devolucionesControls = useTableControls({
    data: devoluciones,
    searchFields: ['Codigo', 'Articulo', 'Cliente', 'Motivo'],
    initialRowsPerPage: 10,
  });

  const clientesControls = useTableControls({
    data: clientes,
    searchFields: ['Cliente', 'NumeroDocumento', 'Estado'],
    initialRowsPerPage: 25,
  });

  // Calcular totales para status cards
  const totalVentas = useMemo(() => {
    if (!dashboardKPIs) return 0;
    return (dashboardKPIs.VentasPagadas || 0) +
      (dashboardKPIs.VentasPendientes || 0) +
      (dashboardKPIs.VentasAnuladas || 0);
  }, [dashboardKPIs]);

  const statusPercentages = useMemo(() => {
    if (totalVentas === 0) return { paid: 0, pending: 0, cancelled: 0 };
    return {
      paid: ((dashboardKPIs?.VentasPagadas || 0) / totalVentas) * 100,
      pending: ((dashboardKPIs?.VentasPendientes || 0) / totalVentas) * 100,
      cancelled: ((dashboardKPIs?.VentasAnuladas || 0) / totalVentas) * 100,
    };
  }, [dashboardKPIs, totalVentas]);

  // ============================================
  // FUNCIONES DE CARGA DE DATOS
  // ============================================
  const cargarDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [kpis, stats, productos, rent, alerts] = await Promise.all([
        ReportesApi.obtenerDashboard({ mesActual: true }),
        ReportesApi.obtenerEstadisticasVentas({}),
        ReportesApi.obtenerTopProductos({ limite: 5 }),
        ReportesApi.obtenerRentabilidad({ limite: 5 }),
        ReportesApi.obtenerReporteAlertas(),
      ]);

      setDashboardKPIs(kpis);
      setEstadisticas(stats);
      setTopProductos(productos);
      setRentabilidad(rent);
      setAlertas(alerts);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error al Cargar Datos',
        text: 'No se pudo cargar la información del dashboard. Por favor, intenta nuevamente.',
        confirmButtonColor: '#0891b2',
        confirmButtonText: 'Reintentar',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          cargarDashboard();
        }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarDatosTab = useCallback(async (tabIndex: number) => {
    setLoading(true);
    try {

      switch (tabIndex) {
        case 0: // Ventas
          const ventasData = await ReportesApi.obtenerReporteVentas({ limite: 1000 });
          setVentas(ventasData);
          break;
        case 1: // Productos
          const [productos, rent, abc] = await Promise.all([
            ReportesApi.obtenerTopProductos({ limite: 100 }),
            ReportesApi.obtenerRentabilidad({ limite: 100 }),
            ReportesApi.obtenerAnalisisABC({}),
          ]);          
          setTopProductos(productos);
          setRentabilidad(rent);
          setAnalisisABC(abc);
          break;
        case 2: // Inventario
          const invData = await ReportesApi.obtenerReporteInventario({ limite: 1000 });          
          setInventario(invData);
          break;
        case 3: // Impuestos
          const impData = await ReportesApi.obtenerReporteImpuestos({});         
          setImpuestos(impData);
          break;
        case 4: // Devoluciones
          const devData = await ReportesApi.obtenerDevolucionesVentas({});          
          setDevoluciones(devData);
          break;
        case 5: // Clientes
          const cliData = await ReportesApi.obtenerReporteClientes({ limite: 1000 });          
          setClientes(cliData);
          break;
        case 6: // Vendedores
          const vendData = await ReportesApi.obtenerReporteVendedores({});         
          setVendedores(vendData);
          break;
      }
    } catch (error) {
      console.error(`Error al cargar datos del tab ${tabIndex}:`, error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
  const handleFiltrosChange = useCallback(async (filtros: FiltroReporte) => {
    try {
      setLoading(true);
      const [stats, productos, rent] = await Promise.all([
        ReportesApi.obtenerEstadisticasVentas(filtros),
        ReportesApi.obtenerTopProductos({ ...filtros, limite: 5 }),
        ReportesApi.obtenerRentabilidad({ ...filtros, limite: 5 }),
      ]);

      setEstadisticas(stats);
      setTopProductos(productos);
      setRentabilidad(rent);

      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });

      Toast.fire({
        icon: 'success',
        title: 'Filtros aplicados correctamente'
      });
    } catch (error) {
      console.error('Error al aplicar filtros:', error);
      toast.error('No se pudieron aplicar los filtros');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    cargarDatosTab(newValue);
  }, [cargarDatosTab]);

  const handleExportMenu = useCallback((event: React.MouseEvent<HTMLElement>, type: string) => {
    setExportAnchorEl(event.currentTarget);
    setCurrentExportType(type);
  }, []);

  const handleCloseExportMenu = useCallback(() => {
    setExportAnchorEl(null);
  }, []);

  const handleExport = useCallback(async (formato: 'excel' | 'csv' | 'pdf') => {
    handleCloseExportMenu();
    setExportLoading(true);

    try {
      switch (currentExportType) {
        case 'ventas':
          if (formato === 'excel') {
            await ExportService.exportarVentasExcel(ventas);
          } else if (formato === 'csv') {
            await ExportService.exportarCSV(ventas, 'reporte-ventas');
          }
          break;
        case 'productos':
          if (formato === 'excel') {
            await ExportService.exportarTopProductosExcel(topProductos);
          }
          break;
        case 'rentabilidad':
          if (formato === 'excel') {
            await ExportService.exportarRentabilidadExcel(rentabilidad);
          }
          break;
        case 'impuestos':
          if (formato === 'excel') {
            await ExportService.exportarImpuestosExcel(impuestos);
          }
          break;
        case 'inventario':
          if (formato === 'excel') {
            await ExportService.exportarInventarioExcel(inventario);
          }
          break;
        case 'consolidado':
          const [v, c, p, r, i] = await Promise.all([
            ReportesApi.obtenerReporteVentas({ limite: 100 }),
            ReportesApi.obtenerComprasPorProveedor({}),
            ReportesApi.obtenerTopProductos({ limite: 20 }),
            ReportesApi.obtenerRentabilidad({ limite: 50 }),
            ReportesApi.obtenerReporteImpuestos({}),
          ]);
          await ExportService.exportarReporteConsolidado({
            ventas: v,
            compras: c,
            productos: p,
            rentabilidad: r,
            impuestos: i,
          });
          break;
      }

      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: 'success',
        title: `Reporte exportado en ${formato.toUpperCase()}`
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('No se pudo exportar el reporte. Por favor, intenta nuevamente.');
    } finally {
      setExportLoading(false);
    }
  }, [currentExportType, ventas, topProductos, rentabilidad, impuestos, inventario, handleCloseExportMenu]);

  const handleRefresh = useCallback(() => {
    cargarDashboard();
    if (tabValue > 0) {
      cargarDatosTab(tabValue);
    }
  }, [cargarDashboard, cargarDatosTab, tabValue]);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    cargarDashboard();
    cargarDatosTab(0);
  }, [cargarDashboard, cargarDatosTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      cargarDashboard();
      if (tabValue > 0) {
        cargarDatosTab(tabValue);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [cargarDashboard, cargarDatosTab, tabValue]);

  // ============================================
  // RENDER
  // ============================================
  if (loading && !dashboardKPIs) {
    return (
      <PageLayout icon={BarChartIcon} title="Reportes y Estadísticas">
          <DashboardSkeleton />
        </PageLayout>
    );
  }

  return (
    <PageLayout icon={BarChartIcon} title="Reportes y Estadísticas">
        <Box className="reportes-container">
          <Container maxWidth="xl">
            {/* Header con botones */}
            <Slide direction="down" in timeout={300}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h4" fontWeight={700} className="text-gradient">
                    Panel de Reportes
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Análisis completo de tu negocio
                    </Typography>
                    <Chip
                      size="small"
                      label={`Actualizado: ${lastUpdate.toLocaleTimeString()}`}
                      variant="outlined"
                      icon={<Insights />}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={2}>
                  <Tooltip title="Recargar datos" arrow>
                    <IconButton
                      onClick={handleRefresh}
                      color="primary"
                      disabled={loading}
                      className="hover-glow"
                    >
                      <Refresh className={loading ? 'spinning' : ''} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<FileDownload />}
                    onClick={(e) => handleExportMenu(e, 'consolidado')}
                    disabled={exportLoading}
                    className="export-button"
                  >
                    Exportar Consolidado
                  </Button>
                </Box>
              </Box>
            </Slide>

            {/* Alertas */}
            {alertas.length > 0 && (
              <Zoom in timeout={500}>
                <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />} className="glass-effect">
                  <AlertTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                      <strong>{alertas.length} Alertas Requieren Atención</strong>
                      <Badge badgeContent={alertas.filter(a => a.Prioridad === 'ALTA').length} color="error" />
                    </Box>
                  </AlertTitle>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    {alertas.slice(0, 3).map((alerta, idx) => (
                      <Chip
                        key={idx}
                        label={alerta.Descripcion}
                        size="small"
                        color={alerta.Prioridad === 'ALTA' ? 'error' : 'warning'}
                        variant="outlined"
                        icon={<Warning />}
                      />
                    ))}
                    {alertas.length > 3 && (
                      <Tooltip title="Ver todas las alertas" arrow>
                        <Chip
                          label={`+${alertas.length - 3} más`}
                          size="small"
                          variant="outlined"
                          clickable
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Alert>
              </Zoom>
            )}

            {/* Filtros */}
            <Fade in timeout={400}>
              <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }} className="glass-effect">
                <ReportFilters onFiltrosChange={handleFiltrosChange} />
              </Paper>
            </Fade>

            {/* KPIs Grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Total Ventas"
                  value={dashboardKPIs?.TotalVentas || 0}
                  subtitle="Este mes"
                  icon={<ShoppingCart />}
                  color="primary"
                  loading={loading}
                  delay={0}
                  trend={12.5}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Monto Total"
                  value={ReportesApi.formatearMoneda(dashboardKPIs?.MontoTotal || 0)}
                  subtitle="Ingresos del mes"
                  icon={<AttachMoney />}
                  color="success"
                  loading={loading}
                  delay={100}
                  trend={8.3}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Ticket Promedio"
                  value={ReportesApi.formatearMoneda(dashboardKPIs?.TicketPromedio || 0)}
                  subtitle="Por venta"
                  icon={<Assessment />}
                  color="warning"
                  loading={loading}
                  delay={200}
                  trend={-2.1}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Clientes Únicos"
                  value={dashboardKPIs?.ClientesUnicos || 0}
                  subtitle="Este mes"
                  icon={<People />}
                  color="primary"
                  loading={loading}
                  delay={300}
                  trend={15.7}
                />
              </Grid>
            </Grid>

            {/* Desglose de Devoluciones */}
            {dashboardKPIs && typeof dashboardKPIs.TotalDevoluciones === 'number' && dashboardKPIs.TotalDevoluciones > 0 && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Fade in timeout={700}>
                  <Alert
                    severity="info"
                    icon={<CompareArrows />}
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'info.main',
                      boxShadow: 2,
                    }}
                    className="glass-effect"
                  >
                    <AlertTitle>
                      <Box display="flex" alignItems="center" gap={1}>
                        <strong>Ajuste por Devoluciones del Período</strong>
                        <Chip
                          label={`${dashboardKPIs.TotalDevoluciones} ${dashboardKPIs.TotalDevoluciones === 1 ? 'devolución' : 'devoluciones'}`}
                          size="small"
                          color="info"
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    </AlertTitle>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            💰 Ventas Brutas
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="text.primary">
                            {ReportesApi.formatearMoneda(dashboardKPIs.MontoTotalBruto || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Antes de devoluciones
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'error.main'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            ↩️ Total Devuelto
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="error.main">
                            -{ReportesApi.formatearMoneda(dashboardKPIs.MontoDevuelto || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {dashboardKPIs.TotalDevoluciones} {dashboardKPIs.TotalDevoluciones === 1 ? 'devolución' : 'devoluciones'} procesadas
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'success.main'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            ✅ Ventas Netas
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="success.main">
                            {ReportesApi.formatearMoneda(dashboardKPIs.MontoTotal)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Monto real de ingresos
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {dashboardKPIs?.MontoTotalBruto != null && dashboardKPIs?.MontoDevuelto != null && dashboardKPIs.MontoTotalBruto > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Impacto de devoluciones
                          </Typography>
                          <Typography variant="caption" color="error.main" fontWeight={700}>
                            {((dashboardKPIs.MontoDevuelto / dashboardKPIs.MontoTotalBruto) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(((dashboardKPIs.MontoDevuelto / dashboardKPIs.MontoTotalBruto) * 100), 100)}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            bgcolor: 'success.light',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: 'error.main'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Alert>
                </Fade>
              </Grid>
            )}

            {/* Estados de Ventas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <StatusCard
                  value={dashboardKPIs?.VentasPagadas || 0}
                  label="Ventas Pagadas"
                  type="paid"
                  percentage={statusPercentages.paid}
                  loading={loading}
                  delay={400}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatusCard
                  value={dashboardKPIs?.VentasPendientes || 0}
                  label="Ventas Pendientes"
                  type="pending"
                  percentage={statusPercentages.pending}
                  loading={loading}
                  delay={500}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatusCard
                  value={dashboardKPIs?.VentasAnuladas || 0}
                  label="Ventas Anuladas"
                  type="cancelled"
                  percentage={statusPercentages.cancelled}
                  loading={loading}
                  delay={600}
                />
              </Grid>
            </Grid>

            {/* Gráfico de Tendencias */}
            {estadisticas.length > 0 && (
              <Fade in timeout={700}>
                <Box
                  sx={{
                    mb: 4,
                    mt: 4,
                    position: 'relative',
                    zIndex: 1,
                    clear: 'both'
                  }}
                >
                  <VentasChart
                    data={estadisticas}
                    titulo="Tendencia de Ventas - Últimos 30 Días"
                    mostrarMonto={true}
                    mostrarVentas={true}
                  />
                </Box>
              </Fade>
            )}

            {/* Tabs de Reportes */}
            <Fade in timeout={800}>
              <Paper
                elevation={1}
                sx={{
                  borderRadius: 2,
                  mt: 4,
                  position: 'relative',
                  zIndex: 50,
                  clear: 'both'
                }}
                className="glass-effect"
              >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Reportes tabs"
                  >
                    <Tab icon={<Receipt />} label="Ventas" iconPosition="start" id="tab-0" aria-controls="tabpanel-0" />
                    <Tab icon={<BarChart />} label="Productos" iconPosition="start" id="tab-1" aria-controls="tabpanel-1" />
                    <Tab icon={<Inventory />} label="Inventario" iconPosition="start" id="tab-2" aria-controls="tabpanel-2" />
                    <Tab icon={<AttachMoney />} label="Impuestos" iconPosition="start" id="tab-3" aria-controls="tabpanel-3" />
                    <Tab icon={<CompareArrows />} label="Devoluciones" iconPosition="start" id="tab-4" aria-controls="tabpanel-4" />
                    <Tab icon={<People />} label="Clientes" iconPosition="start" id="tab-5" aria-controls="tabpanel-5" />
                    <Tab icon={<Assessment />} label="Vendedores" iconPosition="start" id="tab-6" aria-controls="tabpanel-6" />
                  </Tabs>
                </Box>

                {/* ============================================ */}
                {/* TAB 0: VENTAS */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={0}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Reporte Detallado de Ventas
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ventasControls.totalItems} registros encontrados
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={(e) => handleExportMenu(e, 'ventas')}
                        disabled={ventas.length === 0 || exportLoading}
                      >
                        Exportar
                      </Button>
                    </Box>

                    {ventas.length > 0 && (
                      <TableControls
                        searchValue={ventasControls.searchValue}
                        onSearchChange={ventasControls.setSearchValue}
                        currentPage={ventasControls.currentPage}
                        totalPages={ventasControls.totalPages}
                        onPageChange={ventasControls.setCurrentPage}
                        rowsPerPage={ventasControls.rowsPerPage}
                        onRowsPerPageChange={ventasControls.setRowsPerPage}
                        totalItems={ventasControls.totalItems}
                        searchPlaceholder="Buscar por código, cliente, usuario..."
                      />
                    )}

                    {loading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                      </Box>
                    ) : ventasControls.paginatedData.length > 0 ? (
                      <ReportesDataTable
                        data={ventasControls.paginatedData}
                        titulo="Ventas Registradas"
                        loading={loading}
                      />
                    ) : ventasControls.searchValue ? (
                      <Alert severity="info" icon={<Search />}>
                        No se encontraron ventas que coincidan con "{ventasControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info" icon={<Analytics />}>
                        No hay datos de ventas disponibles. Usa los filtros para cargar información.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 1: PRODUCTOS */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={1}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                      <Typography variant="h6" fontWeight={600}>
                        Análisis de Productos
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={(e) => handleExportMenu(e, 'productos')}
                        disabled={topProductos.length === 0 || exportLoading}
                      >
                        Exportar
                      </Button>
                    </Box>

                    {/* Top Productos */}
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Top Productos Más Vendidos
                    </Typography>

                    {topProductos.length > 0 && (
                      <TableControls
                        searchValue={productosControls.searchValue}
                        onSearchChange={productosControls.setSearchValue}
                        currentPage={productosControls.currentPage}
                        totalPages={productosControls.totalPages}
                        onPageChange={productosControls.setCurrentPage}
                        rowsPerPage={productosControls.rowsPerPage}
                        onRowsPerPageChange={productosControls.setRowsPerPage}
                        totalItems={productosControls.totalItems}
                        searchPlaceholder="Buscar productos por nombre, categoría, EAN..."
                      />
                    )}

                    {loading ? (
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {[1, 2, 3].map((i) => (
                          <Grid item xs={12} key={i}>
                            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : productosControls.paginatedData.length > 0 ? (
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {productosControls.paginatedData.map((producto, index) => (
                          <Grid item xs={12} key={producto.Id}>
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                              <Paper sx={{ p: 2 }} elevation={0} variant="outlined" className="hover-lift">
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Chip
                                      label={`#${(productosControls.currentPage - 1) * productosControls.rowsPerPage + index + 1}`}
                                      color="primary"
                                      size="small"
                                      sx={{ fontWeight: 700 }}
                                    />
                                    <Box>
                                      <Typography variant="subtitle1" fontWeight={600}>
                                        {producto.Producto}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {producto.Categoria} • EAN: {producto.EAN}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box textAlign="right">
                                    <Typography variant="h6" color="primary.main" fontWeight={700}>
                                      {producto.CantidadVendida} uds.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {ReportesApi.formatearMoneda(producto.MontoTotal)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Zoom>
                          </Grid>
                        ))}
                      </Grid>
                    ) : productosControls.searchValue ? (
                      <Alert severity="info" icon={<Search />} sx={{ mb: 4 }}>
                        No se encontraron productos que coincidan con "{productosControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mb: 4 }}>
                        No hay datos de productos disponibles
                      </Alert>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Rentabilidad */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Análisis de Rentabilidad
                      </Typography>
                      <Button
                        variant="text"
                        startIcon={<Download />}
                        onClick={(e) => handleExportMenu(e, 'rentabilidad')}
                        size="small"
                        disabled={rentabilidad.length === 0 || exportLoading}
                      >
                        Exportar
                      </Button>
                    </Box>

                    {rentabilidad.length > 0 && (
                      <TableControls
                        searchValue={rentabilidadControls.searchValue}
                        onSearchChange={rentabilidadControls.setSearchValue}
                        currentPage={rentabilidadControls.currentPage}
                        totalPages={rentabilidadControls.totalPages}
                        onPageChange={rentabilidadControls.setCurrentPage}
                        rowsPerPage={rentabilidadControls.rowsPerPage}
                        onRowsPerPageChange={rentabilidadControls.setRowsPerPage}
                        totalItems={rentabilidadControls.totalItems}
                        searchPlaceholder="Buscar productos..."
                      />
                    )}

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2, 3].map((i) => (
                          <Grid item xs={12} key={i}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : rentabilidadControls.paginatedData.length > 0 ? (
                      <Grid container spacing={2}>
                        {rentabilidadControls.paginatedData.map((producto, index) => (
                          <Grid item xs={12} key={producto.Id}>
                            <Grow in timeout={400} style={{ transitionDelay: `${index * 50}ms` }}>
                              <Paper sx={{ p: 2 }} elevation={0} variant="outlined" className="hover-lift">
                                <Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {producto.Producto}
                                    </Typography>
                                    <Chip
                                      label={ReportesApi.formatearPorcentaje(producto.MargenPorcentaje || 0)}
                                      color={(producto.MargenPorcentaje || 0) > 20 ? 'success' : 'warning'}
                                      size="small"
                                      sx={{ fontWeight: 700 }}
                                    />
                                  </Box>
                                  <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        Vendidas
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {producto.CantidadVendida || 0} uds.
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        Ingresos Netos
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} color="primary.main">
                                        {ReportesApi.formatearMoneda(producto.IngresoNeto || 0)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        Costos Netos
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} color="error.main">
                                        {ReportesApi.formatearMoneda(producto.CostoNeto || 0)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        Utilidad Neta
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} color="success.main">
                                        {ReportesApi.formatearMoneda(producto.UtilidadBrutaNeta || 0)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Paper>
                            </Grow>
                          </Grid>
                        ))}
                      </Grid>
                    ) : rentabilidadControls.searchValue ? (
                      <Alert severity="info" icon={<Search />}>
                        No se encontraron productos que coincidan con "{rentabilidadControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info">
                        No hay datos de rentabilidad disponibles
                      </Alert>
                    )}

                    {/* Análisis ABC */}
                    {analisisABC.length > 0 && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Clasificación ABC de Productos
                        </Typography>
                        <Grid container spacing={2}>
                          {analisisABC.map((clasificacion, index) => (
                            <Grid item xs={12} md={4} key={clasificacion.Clasificacion}>
                              <Zoom in timeout={500} style={{ transitionDelay: `${index * 100}ms` }}>
                                <Paper sx={{ p: 3 }} variant="outlined" className="hover-lift glass-effect">
                                  <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
                                    Clase {clasificacion.Clasificacion}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                    {clasificacion.NumeroProductos} productos ({clasificacion.PorcentajeProductos.toFixed(1)}%)
                                  </Typography>
                                  <Box sx={{ mb: 2 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={clasificacion.PorcentajeVentas}
                                      sx={{ height: 8, borderRadius: 1 }}
                                      color={
                                        clasificacion.Clasificacion === 'A' ? 'success' :
                                          clasificacion.Clasificacion === 'B' ? 'warning' : 'error'
                                      }
                                    />
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" mb={1}>
                                    Representa el {clasificacion.PorcentajeVentas.toFixed(1)}% de las ventas
                                  </Typography>
                                  <Typography variant="h6" color="success.main" fontWeight={700}>
                                    {ReportesApi.formatearMoneda(clasificacion.MontoTotal)}
                                  </Typography>
                                </Paper>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 2: INVENTARIO */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={2}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Estado del Inventario
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {inventarioControls.totalItems} productos en inventario
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={(e) => handleExportMenu(e, 'inventario')}
                        disabled={inventario.length === 0 || exportLoading}
                      >
                        Exportar
                      </Button>
                    </Box>

                    {inventario.length > 0 && (
                      <TableControls
                        searchValue={inventarioControls.searchValue}
                        onSearchChange={inventarioControls.setSearchValue}
                        currentPage={inventarioControls.currentPage}
                        totalPages={inventarioControls.totalPages}
                        onPageChange={inventarioControls.setCurrentPage}
                        rowsPerPage={inventarioControls.rowsPerPage}
                        onRowsPerPageChange={inventarioControls.setRowsPerPage}
                        totalItems={inventarioControls.totalItems}
                        searchPlaceholder="Buscar por producto, EAN, categoría..."
                      />
                    )}

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2, 3, 4].map((i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : inventarioControls.paginatedData.length > 0 ? (
                      <Grid container spacing={2}>
                        {inventarioControls.paginatedData.map((item, index) => (
                          <Grid item xs={12} md={6} key={item.Id}>
                            <Grow in timeout={300} style={{ transitionDelay: `${index * 30}ms` }}>
                              <Paper
                                sx={{
                                  p: 2,
                                  borderLeft: 4,
                                  borderColor: item.StockActual <= item.StockMinimo ? 'error.main' : 'success.main'
                                }}
                                variant="outlined"
                                className="hover-lift"
                              >
                                <Box display="flex" justifyContent="space-between">
                                  <Box flex={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {item.Producto}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                      EAN: {item.EAN} • {item.Categoria}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                      <Chip
                                        label={`Stock: ${item.StockActual}`}
                                        size="small"
                                        color={item.StockActual <= item.StockMinimo ? 'error' : 'success'}
                                        sx={{ fontWeight: 600 }}
                                      />
                                      <Chip
                                        label={`Mínimo: ${item.StockMinimo}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                  <Box textAlign="right" ml={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Valor Inventario
                                    </Typography>
                                    <Typography variant="h6" color="primary.main" fontWeight={700}>
                                      {ReportesApi.formatearMoneda(item.ValorInventario)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grow>
                          </Grid>
                        ))}
                      </Grid>
                    ) : inventarioControls.searchValue ? (
                      <Alert severity="info" icon={<Search />}>
                        No se encontraron productos que coincidan con "{inventarioControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info" icon={<Inventory />}>
                        No hay datos de inventario. Haz clic en la pestaña para cargar.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 3: IMPUESTOS (SIN PAGINACIÓN) */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={3}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                      <Typography variant="h6" fontWeight={600}>
                        Informe de Impuestos
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={(e) => handleExportMenu(e, 'impuestos')}
                        disabled={impuestos.length === 0 || exportLoading}
                      >
                        Exportar
                      </Button>
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }} className="glass-effect">
                      <AlertTitle><strong>Declaración Fiscal</strong></AlertTitle>
                      Reporte detallado de impuestos con devoluciones ajustadas para declaración de IVA
                    </Alert>

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2].map((i) => (
                          <Grid item xs={12} key={i}>
                            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : impuestos.length > 0 ? (
                      <Grid container spacing={3}>
                        {impuestos.map((registro, index) => {
                          const totalVentas = Number(registro.TotalVentas) || 0;
                          const totalDevoluciones = Number(registro.TotalDevoluciones) || 0;

                          return (
                            <Grid item xs={12} key={index}>
                              <Fade in timeout={300} style={{ transitionDelay: `${index * 100}ms` }}>
                                <Paper sx={{ p: 3 }} variant="outlined" className="hover-lift glass-effect">
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h5" fontWeight={700}>
                                      Período: {registro.Periodo}
                                    </Typography>
                                    {totalDevoluciones > 0 && (
                                      <Chip
                                        label={`${totalDevoluciones} ${totalDevoluciones === 1 ? 'devolución' : 'devoluciones'}`}
                                        size="medium"
                                        color="warning"
                                        variant="filled"
                                        icon={<CompareArrows />}
                                      />
                                    )}
                                  </Box>

                                  <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>📊</span> Ventas del Período
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                          <Typography variant="caption" color="text.secondary">Total Ventas</Typography>
                                          <Typography variant="h5" fontWeight={700}>{totalVentas}</Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(16, 185, 129, 0.1)', borderRadius: 2, border: 1, borderColor: 'success.main' }}>
                                          <Typography variant="caption" color="text.secondary">Ventas Netas</Typography>
                                          <Typography variant="h5" fontWeight={700} color="success.main">
                                            {ReportesApi.formatearMoneda(registro.VentasNetas)}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', borderRadius: 2, border: 1, borderColor: 'error.main' }}>
                                          <Typography variant="caption" color="text.secondary">Devoluciones</Typography>
                                          <Typography variant="h5" fontWeight={700} color="error.main">{totalDevoluciones}</Typography>
                                        </Paper>
                                      </Grid>
                                    </Grid>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>💰</span> Base Gravable
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                          <Typography variant="caption" color="text.secondary">Base Bruta</Typography>
                                          <Typography variant="h6" fontWeight={700}>
                                            {ReportesApi.formatearMoneda(registro.BaseGravableBruta)}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(8, 145, 178, 0.1)', borderRadius: 2, border: 2, borderColor: 'primary.main' }}>
                                          <Typography variant="caption" color="text.secondary">Base Neta</Typography>
                                          <Typography variant="h6" fontWeight={700} color="primary.main">
                                            {ReportesApi.formatearMoneda(registro.BaseGravableNeta)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                            (después de devoluciones)
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                      {registro.ImpuestoDevuelto > 0 && (
                                        <Grid item xs={12} sm={4}>
                                          <Paper sx={{ p: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', borderRadius: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Impuesto Devuelto</Typography>
                                            <Typography variant="h6" fontWeight={700} color="error.main">
                                              -{ReportesApi.formatearMoneda(registro.ImpuestoDevuelto)}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>📈</span> Impuestos (IVA 19%)
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={6}>
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(245, 158, 11, 0.1)', borderRadius: 2 }}>
                                          <Typography variant="caption" color="text.secondary">IVA Bruto</Typography>
                                          <Typography variant="h6" fontWeight={700} color="warning.main">
                                            {ReportesApi.formatearMoneda(registro.ImpuestoBruto)}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', borderRadius: 2, border: 2, borderColor: 'error.main' }}>
                                          <Typography variant="caption" color="text.secondary">IVA Neto a Declarar</Typography>
                                          <Typography variant="h6" fontWeight={700} color="error.main">
                                            {ReportesApi.formatearMoneda(registro.ImpuestoNeto)}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    </Grid>
                                  </Box>

                                  {totalDevoluciones > 0 && registro.DiferenciaPorDevoluciones > 0 && (
                                    <>
                                      <Divider sx={{ my: 2 }} />
                                      <Paper sx={{ p: 3, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2, border: 1, borderColor: 'info.main' }}>
                                        <Box display="flex" alignItems="start" gap={2}>
                                          <Box sx={{ fontSize: '2rem' }}>💡</Box>
                                          <Box flex={1}>
                                            <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>
                                              Diferencia por devoluciones
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color="info.main" gutterBottom>
                                              {ReportesApi.formatearMoneda(registro.DiferenciaPorDevoluciones)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              Este período tiene <strong>{totalDevoluciones}</strong> devolución{totalDevoluciones === 1 ? '' : 'es'} que
                                              ajustaron el impuesto en <strong>{ReportesApi.formatearMoneda(registro.DiferenciaPorDevoluciones)}</strong>
                                            </Typography>
                                            <Chip label="Ahorro por devoluciones procesadas" size="small" color="info" sx={{ mt: 1, fontWeight: 600 }} />
                                          </Box>
                                        </Box>
                                      </Paper>
                                    </>
                                  )}

                                  <Box mt={3}>
                                    <Paper sx={{ p: 3, bgcolor: 'rgba(16, 185, 129, 0.05)', borderRadius: 2, border: 2, borderColor: 'success.main' }}>
                                      <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>📋</span> Resumen para Declaración Fiscal
                                      </Typography>
                                      <Box sx={{ mt: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                                          <Typography variant="body2" color="text.secondary">Base Gravable Neta:</Typography>
                                          <Typography variant="h6" fontWeight={700}>
                                            {ReportesApi.formatearMoneda(registro.BaseGravableNeta)}
                                          </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                                          <Typography variant="body2" color="text.secondary">IVA Neto (19%):</Typography>
                                          <Typography variant="h6" fontWeight={700} color="error.main">
                                            {ReportesApi.formatearMoneda(registro.ImpuestoNeto)}
                                          </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box display="flex" justifyContent="space-between" alignItems="center" py={1} sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', px: 2, borderRadius: 1 }}>
                                          <Typography variant="body1" fontWeight={700}>Total a Declarar:</Typography>
                                          <Typography variant="h5" fontWeight={700} color="success.main">
                                            {ReportesApi.formatearMoneda(registro.VentasNetas)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Paper>
                                  </Box>
                                </Paper>
                              </Fade>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Alert severity="info">
                        No hay datos de impuestos. Haz clic en la pestaña para cargar.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 4: DEVOLUCIONES */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={4}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Devoluciones de Ventas
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {devolucionesControls.totalItems} devoluciones registradas
                        </Typography>
                      </Box>
                    </Box>

                    {devoluciones.length > 0 && (
                      <TableControls
                        searchValue={devolucionesControls.searchValue}
                        onSearchChange={devolucionesControls.setSearchValue}
                        currentPage={devolucionesControls.currentPage}
                        totalPages={devolucionesControls.totalPages}
                        onPageChange={devolucionesControls.setCurrentPage}
                        rowsPerPage={devolucionesControls.rowsPerPage}
                        onRowsPerPageChange={devolucionesControls.setRowsPerPage}
                        totalItems={devolucionesControls.totalItems}
                        searchPlaceholder="Buscar por código, artículo, cliente..."
                      />
                    )}

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2, 3].map((i) => (
                          <Grid item xs={12} key={i}>
                            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : devolucionesControls.paginatedData.length > 0 ? (
                      <Grid container spacing={2}>
                        {devolucionesControls.paginatedData.map((dev, index) => (
                          <Grid item xs={12} key={dev.Id}>
                            <Grow in timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                              <Paper sx={{ p: 2 }} variant="outlined" className="hover-lift">
                                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                  <Box flex={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {dev.Codigo} - {dev.Articulo}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                      Cliente: {dev.Cliente || 'N/A'} • Fecha: {new Date(dev.FechaDevolucion).toLocaleDateString()}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                      <Chip label={dev.Motivo} size="small" variant="outlined" />
                                      <Chip
                                        label={dev.Estado}
                                        size="small"
                                        color={dev.Estado === 'PROCESADA' ? 'success' : 'warning'}
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </Box>
                                  </Box>
                                  <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">
                                      Cantidad: {dev.Cantidad}
                                    </Typography>
                                    <Typography variant="h6" color="error.main" fontWeight={700}>
                                      {ReportesApi.formatearMoneda(dev.Monto)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grow>
                          </Grid>
                        ))}
                      </Grid>
                    ) : devolucionesControls.searchValue ? (
                      <Alert severity="info" icon={<Search />}>
                        No se encontraron devoluciones que coincidan con "{devolucionesControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info" icon={<CompareArrows />}>
                        No hay devoluciones registradas. Haz clic en la pestaña para cargar.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 5: CLIENTES */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={5}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Análisis de Clientes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {clientesControls.totalItems} clientes activos
                        </Typography>
                      </Box>
                    </Box>

                    {clientes.length > 0 && (
                      <TableControls
                        searchValue={clientesControls.searchValue}
                        onSearchChange={clientesControls.setSearchValue}
                        currentPage={clientesControls.currentPage}
                        totalPages={clientesControls.totalPages}
                        onPageChange={clientesControls.setCurrentPage}
                        rowsPerPage={clientesControls.rowsPerPage}
                        onRowsPerPageChange={clientesControls.setRowsPerPage}
                        totalItems={clientesControls.totalItems}
                        searchPlaceholder="Buscar por nombre, documento..."
                      />
                    )}

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2, 3, 4].map((i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : clientesControls.paginatedData.length > 0 ? (
                      <Grid container spacing={2}>
                        {clientesControls.paginatedData.map((cliente, index) => (
                          <Grid item xs={12} md={6} key={cliente.Id}>
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 40}ms` }}>
                              <Paper sx={{ p: 2 }} variant="outlined" className="hover-lift glass-effect">
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {cliente.Cliente}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                                  {cliente.TipoDocumento}: {cliente.NumeroDocumento}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Compras</Typography>
                                    <Typography variant="body2" fontWeight={600}>{cliente.TotalCompras}</Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Monto Total</Typography>
                                    <Typography variant="body2" fontWeight={600} color="primary.main">
                                      {ReportesApi.formatearMoneda(cliente.MontoTotal)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Chip
                                      label={cliente.Estado}
                                      size="small"
                                      color={
                                        cliente.Estado === 'ACTIVO' ? 'success' :
                                          cliente.Estado === 'OCASIONAL' ? 'warning' : 'error'
                                      }
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Zoom>
                          </Grid>
                        ))}
                      </Grid>
                    ) : clientesControls.searchValue ? (
                      <Alert severity="info" icon={<Search />}>
                        No se encontraron clientes que coincidan con "{clientesControls.searchValue}"
                      </Alert>
                    ) : (
                      <Alert severity="info" icon={<People />}>
                        No hay datos de clientes. Haz clic en la pestaña para cargar.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 6: VENDEDORES (SIN PAGINACIÓN) */}
                {/* ============================================ */}
                <TabPanel value={tabValue} index={6}>
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Performance de Vendedores
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vendedores.length} vendedores activos
                        </Typography>
                      </Box>
                    </Box>

                    {loading ? (
                      <Grid container spacing={2}>
                        {[1, 2, 3, 4].map((i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : vendedores.length > 0 ? (
                      <Grid container spacing={2}>
                        {vendedores.map((vendedor, index) => (
                          <Grid item xs={12} md={6} key={vendedor.Id}>
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 40}ms` }}>
                              <Paper sx={{ p: 2 }} variant="outlined" className="hover-lift glass-effect">
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Chip label={`#${index + 1}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                                    <Typography variant="subtitle1" fontWeight={600}>{vendedor.Vendedor}</Typography>
                                  </Box>
                                  {vendedor.Cumplimiento && (
                                    <Chip
                                      label={`${vendedor.Cumplimiento.toFixed(1)}% meta`}
                                      size="small"
                                      color={vendedor.Cumplimiento >= 100 ? 'success' : vendedor.Cumplimiento >= 75 ? 'warning' : 'error'}
                                      sx={{ fontWeight: 700 }}
                                    />
                                  )}
                                </Box>
                                <Grid container spacing={2} mb={1}>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Ventas</Typography>
                                    <Typography variant="body2" fontWeight={600}>{vendedor.TotalVentas}</Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Monto Total</Typography>
                                    <Typography variant="body2" fontWeight={600} color="primary.main">
                                      {ReportesApi.formatearMoneda(vendedor.MontoTotal)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Comisión</Typography>
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                      {ReportesApi.formatearMoneda(vendedor.Comision || 0)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                {vendedor.Meta && vendedor.Cumplimiento && (
                                  <Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(vendedor.Cumplimiento, 100)}
                                      color={vendedor.Cumplimiento >= 100 ? 'success' : vendedor.Cumplimiento >= 75 ? 'warning' : 'error'}
                                      sx={{ height: 6, borderRadius: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Meta: {ReportesApi.formatearMoneda(vendedor.Meta)}
                                    </Typography>
                                  </Box>
                                )}
                              </Paper>
                            </Zoom>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Alert severity="info" icon={<Assessment />}>
                        No hay datos de vendedores. Haz clic en la pestaña para cargar.
                      </Alert>
                    )}
                  </Box>
                </TabPanel>
              </Paper>
            </Fade>
          </Container>

          {/* Menú de exportación */}
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleCloseExportMenu}
            TransitionComponent={Fade}
            PaperProps={{
              elevation: 8,
              sx: {
                borderRadius: 2,
                minWidth: 200,
                mt: 1,
              }
            }}
          >
            <MenuItem onClick={() => handleExport('excel')} disabled={exportLoading}>
              <Download sx={{ mr: 1.5, fontSize: 20 }} />
              Exportar a Excel
            </MenuItem>
            <MenuItem onClick={() => handleExport('csv')} disabled={exportLoading}>
              <Download sx={{ mr: 1.5, fontSize: 20 }} />
              Exportar a CSV
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={() => handleExport('pdf')} disabled>
              <Download sx={{ mr: 1.5, fontSize: 20, opacity: 0.5 }} />
              Exportar a PDF
              <Chip label="Próximamente" size="small" sx={{ ml: 1 }} />
            </MenuItem>
          </Menu>

          {/* Loading overlay mejorado */}
          {(loading || exportLoading) && dashboardKPIs && (
            <Fade in timeout={300}>
              <Box
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  backdropFilter: 'blur(8px)',
                }}
                className="loading-overlay"
              >
                <Zoom in timeout={400}>
                  <Paper
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: 3,
                      minWidth: 280,
                    }}
                    elevation={12}
                    className="glass-effect"
                  >
                    <Box position="relative">
                      <CircularProgress size={56} thickness={4} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        {exportLoading ? <FileDownload /> : <Refresh />}
                      </Box>
                    </Box>
                    <Typography variant="h6" fontWeight={600} textAlign="center">
                      {exportLoading ? 'Exportando reporte...' : 'Cargando datos...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      {exportLoading
                        ? 'Preparando tu archivo para descarga'
                        : 'Por favor espera un momento'}
                    </Typography>
                  </Paper>
                </Zoom>
              </Box>
            </Fade>
          )}
        </Box>
      </PageLayout>
  );
};

export default Reportes;